import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { getMaterials, getProjectCategories } from '../../services/lookups';
import { useFilters, PRICE_FILTERS } from '../../hooks/projectFilters';

import {
  ClearButton,
  Divider,
  FilterOption,
  FiltersContainer,
  GroupTitle,
  Headline,
  HeadlineContainer,
} from './styles';

const propTypes = {
  // O painel é montado duas vezes na mesma página (coluna e gaveta do mobile);
  // o prefixo mantém ids e o grupo de rádio únicos entre as duas instâncias.
  idPrefix: PropTypes.string,
};

const defaultProps = {
  idPrefix: 'filters',
};

const ProjectsFilters = ({ idPrefix }) => {
  const {
    selectedCategories,
    setSelectedCategories,
    selectedMaterials,
    setSelectedMaterials,
    premium,
    setPremium,
    price,
    setPrice,
    setCurrentPage,
  } = useFilters();

  const [categories, setCategories] = useState([]);
  const [materials, setMaterials] = useState([]);

  useEffect(() => {
    let cancelled = false;

    getProjectCategories()
      .then(data => !cancelled && setCategories(data))
      .catch(() => undefined);

    getMaterials()
      .then(data => !cancelled && setMaterials(data))
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, []);

  // Qualquer mudança de filtro reinicia a paginação: continuar na página 5 de
  // um resultado que agora tem 2 páginas mostraria uma lista vazia.
  function backToFirstPage() {
    setCurrentPage(1);
  }

  function handleMaterialCheckboxChange(e) {
    backToFirstPage();
    if (e.target.checked) {
      setSelectedMaterials([...selectedMaterials, e.target.value]);
    } else {
      setSelectedMaterials(selectedMaterials.filter(i => i !== e.target.value));
    }
  }

  function handleCategoryCheckboxChange(e) {
    backToFirstPage();
    if (e.target.checked) {
      setSelectedCategories([...selectedCategories, e.target.value]);
    } else {
      setSelectedCategories(
        selectedCategories.filter(i => i !== e.target.value)
      );
    }
  }

  function handlePremiumCheckboxChange(e) {
    backToFirstPage();
    setPremium(e.target.checked);
  }

  function handlePriceChange(value) {
    backToFirstPage();
    setPrice(value);
  }

  function handleClearClick() {
    backToFirstPage();
    setSelectedCategories([]);
    setSelectedMaterials([]);
    setPremium(false);
    setPrice('all');
  }

  const hasActiveFilters =
    selectedCategories.length > 0 ||
    selectedMaterials.length > 0 ||
    premium ||
    price !== 'all';

  return (
    <FiltersContainer>
      <HeadlineContainer>
        <Headline>Filtros</Headline>
        {hasActiveFilters && (
          <ClearButton type="button" onClick={handleClearClick}>
            Limpar
          </ClearButton>
        )}
      </HeadlineContainer>

      <GroupTitle>Preço</GroupTitle>
      {PRICE_FILTERS.map(option => (
        <FilterOption
          key={option.value}
          htmlFor={`${idPrefix}-price-${option.value}`}
        >
          <input
            id={`${idPrefix}-price-${option.value}`}
            type="radio"
            name={`${idPrefix}-price`}
            value={option.value}
            checked={price === option.value}
            onChange={() => handlePriceChange(option.value)}
          />
          {option.label}
        </FilterOption>
      ))}
      <Divider />

      <GroupTitle>Categorias</GroupTitle>
      {categories.map(c => (
        <FilterOption key={c.id} htmlFor={`${idPrefix}-category-${c.id}`}>
          <input
            id={`${idPrefix}-category-${c.id}`}
            type="checkbox"
            value={c.id}
            checked={!!selectedCategories.find(e => +e === c.id)}
            onChange={handleCategoryCheckboxChange}
          />
          {c.name}
        </FilterOption>
      ))}
      <Divider />

      <GroupTitle>Materiais</GroupTitle>
      {materials.map(m => (
        <FilterOption key={m.id} htmlFor={`${idPrefix}-material-${m.id}`}>
          <input
            id={`${idPrefix}-material-${m.id}`}
            type="checkbox"
            value={m.id}
            checked={!!selectedMaterials.find(e => +e === m.id)}
            onChange={handleMaterialCheckboxChange}
          />
          {m.name}
        </FilterOption>
      ))}
      <Divider />

      <GroupTitle>Assinatura</GroupTitle>
      <FilterOption htmlFor={`${idPrefix}-premium`}>
        <input
          id={`${idPrefix}-premium`}
          type="checkbox"
          checked={premium}
          onChange={handlePremiumCheckboxChange}
        />
        Somente Pro
      </FilterOption>
    </FiltersContainer>
  );
};

ProjectsFilters.propTypes = propTypes;
ProjectsFilters.defaultProps = defaultProps;

export default ProjectsFilters;
